import { WEEKDAYS } from "@/utils/dates";
import { formatNumber } from "@/utils/number-utils";
import {
  Control,
  Controller,
  FieldError,
  FieldPathByValue,
  FieldValues,
} from "react-hook-form";
import { NumberField } from "../generic/NumberField";

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPathByValue<
    T,
    { [m in (typeof WEEKDAYS)[number]["id"]]?: number | null } | undefined
  >;
  errorMessage?: string | FieldError;
  placeholders?: Partial<
    Record<(typeof WEEKDAYS)[number]["id"], number | null>
  >;
};

export function LeadScheduleInputs<T extends FieldValues>({
  control,
  name,
  errorMessage,
  placeholders,
}: Props<T>) {
  const getPlaceholder = (day: (typeof WEEKDAYS)[number]["id"]) => {
    const placeholder = placeholders?.[day];
    return formatNumber(placeholder ?? 0);
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <div className="grid grid-cols-5 items-center gap-x-4 gap-y-2">
          {WEEKDAYS.map((day) => (
            <div key={day.id} className="flex flex-col gap-y-4">
              <span className="text-sm font-medium">{day.name}</span>
              <NumberField
                aria-label={`Antal leads for ${day.name}`}
                inputClassName="w-full max-w-32 text-left focus:placeholder:opacity-0 placeholder:font-bold"
                placeholder={getPlaceholder(day.id)}
                value={field.value?.[day.id] ?? null}
                onChange={(dayValue) =>
                  field.onChange({ ...(field.value ?? {}), [day.id]: dayValue })
                }
                size="sm"
                minValue={0}
                maxDecimals={0}
              />
            </div>
          ))}

          {(error?.message || errorMessage) && (
            <div className="col-span-5 text-[11px] text-red-600">
              {error?.message || errorMessage?.toString()}
            </div>
          )}
        </div>
      )}
    />
  );
}
